import { Point2D, orientation, eqPoint, IntersectionResult, intersection, IntersectionType, pointOnSegment, getSegmentEndPointIndex } from '../geometry/geometry';
import { circularNext, Polygon, Side } from '../geometry/polygon';
import { BinarySearchTree } from '../util/binary-search-tree';

/*
  * Based on Joseph O-Rourke, Computational Geometry in C. Chapter 7.7
  *
  * We use PlaneSweep/ SweepLine to find all intersections among a
  * set of line segments.
  */

/**
 * When iVertical0 is the first vertex in polygon order then the inside of the polygon is to the right
 *  of the segment. When iVertical1 is the first vertex in polygon order the inside of the polygon
 * is to the left of the segment.
 */
export interface Segment {
	id?: string,//the id of the (remaining part of the) segment 
	sourceId: number, //the fixed initial id of the segment (the source id)
	setSides: { [key: number]: Side },// maps the id of the set/polygon, where this segment belongs to, to the side at which the inside of the polygon lies
	iVertical0: number, //highest segment vertex index
	iVertical1: number,//lowest segment vertex index
	iIntersection0: number, // start vertex index to use in intersection tests
	iIntersection1: number // end vertex index to use in intersection tests
}

type SegmentMap = { [key: number]: Segment };

interface SweepLineEvent {
	id: string,
	vertex: Point2D,
	vertexIndex: number,
	vertexId: string,
	add: BinarySearchTree<number>,
	remove: BinarySearchTree<number>,
	intersect: BinarySearchTree<number>
}
const segmentToString = (seg: Segment): string => {
	return `S${seg.sourceId}: ${seg.iVertical0} -> ${seg.iVertical1} / ${Object.entries(seg.setSides).map(entry => entry[0] + (entry[1] === Side.LEFT ? 'L' : 'R')).join(',')}`
}
const segmentsToString = (segs: Segment[]): string => {
	return segs.map(seg => segmentToString(seg)).join('\n');
}
const sweepLineToDumpString = (sweepLine: BinarySearchTree<number>, segments: SegmentMap): string => {
	return segmentsToString(Array.from(sweepLine.inOrder()).map(segKey => segments[segKey]));
}
const verticesToDumpString = (vertices: Point2D[]): string => {
	return vertices.map((vertex, index) => `${index}: [ ${vertex[0]}, ${vertex[1]} ]`).join('\n');
}

const mergeSegments = (target: Segment | object, source: Segment): Segment => {
	const setSides = Object.assign(target && (<Segment>target).setSides ? (<Segment>target).setSides : <{ [key: number]: Side }>{}, source.setSides);
	Object.assign(target, source);
	(<Segment>target).setSides = setSides;
	return <Segment>target;
}

//orders vertices top to bottom and left to right
export const vertexComparator = (v0: Point2D, v1: Point2D): number => {
	const yDiff = v1[1] - v0[1];
	if (yDiff === 0) {
		return v0[0] - v1[0]; //xDiff: NB lower x comes first
	} else {
		return yDiff;
	}
}
const sweepLineEventComparator = (ev0: SweepLineEvent, ev1: SweepLineEvent): number => {
	const vertexComparison = vertexComparator(ev0.vertex, ev1.vertex);
	return vertexComparison;
}
export const polygonsToSegments = (polygons: Polygon[]): { vertices: Point2D[], segments: Segment[] } => {
	const vertices: Point2D[] = [];

	const vertexMap: Map<Symbol, number> = new Map();

	const segments = polygons.flatMap((polygon, polygonIndex) => {
		const polygonSegments = polygon.paths.flatMap((path, pathIndex) => {
			return path.map((vertexIndex0, pathVertexIndex) => {
				const vertexIndex1 = path[circularNext(pathVertexIndex, path)];
				const iVertical0IsI0 = vertexComparator(polygon.vertices[vertexIndex0], polygon.vertices[vertexIndex1]) < 0;

				const v0 = polygon.vertices[vertexIndex0];
				const v1 = polygon.vertices[vertexIndex1];

				const s0 = Symbol.for(JSON.stringify(v0));
				const s1 = Symbol.for(JSON.stringify(v1));

				let i0 = vertexMap.get(s0);
				if (i0 === undefined) {
					i0 = vertices.length;
					vertices.push(v0);
					vertexMap.set(s0, i0);
				}
				let i1 = vertexMap.get(s1);
				if (i1 === undefined) {
					i1 = vertices.length;
					vertices.push(v1);
					vertexMap.set(s1, i1);
				}

				const setSides: { [key: number]: Side } = {};
				setSides[polygonIndex] = iVertical0IsI0 ? Side.RIGHT : Side.LEFT;
				return <Segment>{
					id: `p${polygonIndex}_${pathIndex}_${pathVertexIndex}`,
					sourceId: 0,
					setSides: setSides,
					leftSets: [],
					rightSets: [],
					iVertical0: iVertical0IsI0 ? i0 : i1,
					iVertical1: iVertical0IsI0 ? i1 : i0,
					iIntersection0: i0,
					iIntersection1: i1
				};
			});
		});
		return polygonSegments;
	});
	segments.forEach((segment, index) => segment.sourceId = index);
	return { vertices: vertices, segments: segments };
}

const createSegmentLine = (vertices: Point2D[], segments: SegmentMap, data?: number | number[] | BinarySearchTree<number>): BinarySearchTree<number> =>
	new BinarySearchTree<number>(
		segmentKeyHorizontalPositionComparator.bind(this, vertices, segments),
		(segmentKey, newSegmentKey) => {
			const segment = segments[segmentKey];
			const newSegment = segments[newSegmentKey];
			Object.assign(segment.setSides, newSegment.setSides);
			segments[newSegmentKey] = segment;//update the reference map as well
		},
		data);

const segmentToSweepLineEvents = (vertices: Point2D[], segments: SegmentMap, segmentKey: number): [SweepLineEvent, SweepLineEvent] => {
	const segment = segments[segmentKey];
	return [
		{
			id: 'A_' + segment.id,
			vertex: vertices[segment.iVertical0],
			vertexIndex: segment.iVertical0,
			vertexId: 'v' + segment.iVertical0,
			add: createSegmentLine(vertices, segments, segmentKey),
			remove: createSegmentLine(vertices, segments),
			intersect: createSegmentLine(vertices, segments)
		},
		{
			id: 'R_' + segment.id,
			vertex: vertices[segment.iVertical1],
			vertexIndex: segment.iVertical1,
			vertexId: 'v' + segment.iVertical1,
			add: createSegmentLine(vertices, segments),
			remove: createSegmentLine(vertices, segments, segmentKey),
			intersect: createSegmentLine(vertices, segments)
		}];
}
const segmentIntersectionTester = (vertices: Point2D[], segments: SegmentMap, sKey0: number, sKey1: number): IntersectionResult => {
	const s0 = sKey0 !== undefined ? segments[sKey0] : undefined;
	const s1 = sKey1 !== undefined ? segments[sKey1] : undefined;
	return intersection(vertices[s0.iIntersection0], vertices[s0.iIntersection1], vertices[s1.iIntersection0], vertices[s1.iIntersection1]);
}

const segmentKeyHorizontalPositionComparator = (vertices: Point2D[], segments: SegmentMap, sKey0: number, sKey1: number): number => {
	const s0 = segments[sKey0];
	const s1 = segments[sKey1];
	return segmentHorizontalPositionComparator(vertices, s0, s1);
}

export const segmentHorizontalPositionComparator = (vertices: Point2D[], s0: Segment, s1: Segment): number => {
	const s0IsLower = (vertices[s1.iVertical0][1] - vertices[s0.iVertical0][1]) > 0; //when > 0 then s0 starts at a lower  y coordinate then s1
	const testSegmentStart = s0IsLower ? vertices[s0.iVertical0] : vertices[s1.iVertical0];
	const otherSegmentStart = s0IsLower ? vertices[s1.iVertical0] : vertices[s0.iVertical0];
	const otherSegmentEnd = s0IsLower ? vertices[s1.iVertical1] : vertices[s0.iVertical1];

	const orientationSegmentStart = orientation(testSegmentStart, otherSegmentStart, otherSegmentEnd);

	let testResult: number;

	if (orientationSegmentStart < 0) {
		//vertices lie in clock wise order ->  test segment starts left from other segment 
		//return  the test segment as first
		testResult = -1;
	} else if (orientationSegmentStart > 0) {
		//vertices lie in counter clock wise order -> test segment starts right from other segment 
		//return the other segement as first
		testResult = 1;
	} else {
		//vertices are colinear -> test segment starts on other segment
		const testSegmentEnd = s0IsLower ? vertices[s0.iVertical1] : vertices[s1.iVertical1];
		const orientationSegmentEnd = orientation(testSegmentEnd, otherSegmentStart, otherSegmentEnd);
		if (orientationSegmentEnd < 0) {
			//vertices lie in clock wise order ->  test segment ends left from other segment
			//return  the test segment as first
			testResult = -1;
		} else if (orientationSegmentEnd > 0) {
			//vertices lie in counter clock wise order -> test segment ends right from other segment
			//return the other segement as first 
			testResult = 1;
		} else {
			//vertices are colinear -> test segment and other segment are completly colinear
			if (eqPoint(testSegmentStart, otherSegmentStart) && eqPoint(testSegmentEnd, otherSegmentEnd)) {
				//it is exactly the same segment
				testResult = 0;
			} else {
				//return the test segment as first
				testResult = -1;
			}
		}

	}

	return testResult * (s0IsLower ? 1 : -1);
}

export interface SweepMessage {
	nextEvent: SweepLineEvent,
	event: SweepLineEvent,
	segments:  Segment[]
}

const handleEvent = (event: SweepLineEvent, sweepLine: BinarySearchTree<number>, eventQueue: BinarySearchTree<SweepLineEvent>, vertices: Point2D[], segments: SegmentMap, intersectionTester: (s0: number, s1: number) => IntersectionResult, intersectionDrawer?: (vertices: Point2D[], s0: Segment, s1: Segment, i: Point2D) => void): SweepMessage => {
	let emittedSegments = new BinarySearchTree<Segment>(segmentHorizontalPositionComparator.bind(this, vertices),
			(segment, newSegment) => {
			Object.assign(segment.setSides, newSegment.setSides);
		});

	//first perform the intersecting
	emittedSegments.addAll(intersectSegments(event, sweepLine, eventQueue, vertices, segments, intersectionTester, true, intersectionDrawer));
	//then remove segments that need to be removed (this might generate an intersection  of the bottom/event vertex with one or more of the existing segments, which is dealt with inside this handler). 
	emittedSegments.addAll(removeSegment(event, sweepLine, eventQueue, vertices, segments, intersectionTester, intersectionDrawer));
	//finally add segments (this might generate an intersection  of the top/event vertex with one or more of the existing segments, which is dealt with inside this handler).
	emittedSegments.addAll(addSegment(event, sweepLine, eventQueue, vertices, segments, intersectionTester, intersectionDrawer));

	eventQueue.delete(event);
	const nextEvent = eventQueue.min();
	
	//console.log(Array.from(emittedSegments.inOrder()).map(segment => segmentToString(segment)).join('  |  '));
	
	return {
		nextEvent: nextEvent,
		event: event,
		segments: Array.from(emittedSegments.inOrder())
	};
}

const processSegmentEventVertex = (segmentKey: number, segmentIndex: number, eventVertexIndex: number, sweepLine: BinarySearchTree<number>, emittedSegments: Segment[], vertices: Point2D[], segments: SegmentMap): void => {
	const eventVertex = vertices[eventVertexIndex];
	const lp0 = vertices[segments[segmentKey].iVertical0];
	const lp1 = vertices[segments[segmentKey].iVertical1];
	if (pointOnSegment(lp0, lp1, eventVertex) && getSegmentEndPointIndex(lp0, lp1, eventVertex) === -1) {
		//the event vertex lies on the segment referenced by segmentKey and is not one of the segment's end points
		const intersectedSegment = cutSegment(segmentKey, vertices, segments, eventVertexIndex, segmentIndex + emittedSegments.length);
		emittedSegments.push(intersectedSegment.clippedSegment);
		//remove original segment from sweepline
		sweepLine.delete(intersectedSegment.originalSegment);
		//update the original segment to its shortened version'
		segments[intersectedSegment.originalSegment] = mergeSegments(segments[intersectedSegment.originalSegment], intersectedSegment.remainingSegment);
		//add the updated segment to the seeepline  
		sweepLine.add(intersectedSegment.originalSegment);
	}
}

const processEventVertex = (segmentKey: number, sweepLine: BinarySearchTree<number>, eventQueue: BinarySearchTree<SweepLineEvent>, vertices: Point2D[], segments: SegmentMap, segmentIndex: number): Segment[] => {
	const emittedSegments: Segment[] = [];

	const eventVertexIndex = eventQueue.min().vertexIndex;

	const prev = sweepLine.prev(segmentKey);
	if (prev !== undefined) {
		processSegmentEventVertex(prev, segmentIndex, eventVertexIndex, sweepLine, emittedSegments, vertices, segments);
	}

	const next = sweepLine.next(segmentKey);
	if (next !== undefined) {
		processSegmentEventVertex(next, segmentIndex, eventVertexIndex, sweepLine, emittedSegments, vertices, segments);
	}

	return emittedSegments;
}

const addSegment = (swEv: SweepLineEvent, sweepLine: BinarySearchTree<number>, eventQueue: BinarySearchTree<SweepLineEvent>, vertices: Point2D[], segments: SegmentMap, intersectionTester: (s0: number, s1: number) => IntersectionResult, intersectionDrawer?: (vertices: Point2D[], s0: Segment, s1: Segment, i: Point2D) => void): Segment[] => {
	const emittedSegments: Segment[] = [];
	if (!swEv.add.isEmpty()) {

		const first = swEv.add.min();
		const last = swEv.add.max();

		sweepLine.add(first);
		swEv.add.delete(first);

		//check if the segments start on an existing segment (i.e. does the event vertex lie on an existing segment) (intersection type = VERTEX)
		//if it does, cut that segment at the event vertex
		//repeat until no lines can be found
		let emitCount = 0;
		do {
			emitCount = emittedSegments.length;
			emittedSegments.splice(emittedSegments.length, 0, ...processEventVertex(first, sweepLine, eventQueue, vertices, segments, emitCount));
		} while (emitCount < emittedSegments.length);


		sweepLine.addAll(swEv.add);
		swEv.add.clear();

		const prev = sweepLine.prev(first);
		const next = sweepLine.next(last);

		doIntersectionTest(prev, first, eventQueue, vertices, segments, intersectionTester, intersectionDrawer);
		doIntersectionTest(last, next, eventQueue, vertices, segments, intersectionTester, intersectionDrawer);

	}
	return emittedSegments;
}


const removeSegment = (swEv: SweepLineEvent, sweepLine: BinarySearchTree<number>, eventQueue: BinarySearchTree<SweepLineEvent>, vertices: Point2D[], segments: SegmentMap, intersectionTester: (s0: number, s1: number) => IntersectionResult, intersectionDrawer?: (vertices: Point2D[], s0: Segment, s1: Segment, i: Point2D) => void): Segment[] => {
	const emittedSegments: Segment[] = [];
	if (!swEv.remove.isEmpty()) {
		//construct a new delete tree to eliminate segments that are now equal due to possible event vertex intersections
		const toRemove = createSegmentLine(vertices, segments, swEv.remove);
		swEv.remove.clear();

		const first = toRemove.min();
		const last = toRemove.max();

		const prev = sweepLine.prev(first);
		const next = sweepLine.next(last);

		toRemove.delete(first);

		const removedKeys = sweepLine.deleteAll(toRemove);

		emittedSegments.splice(0, 0, ...removedKeys.map(segmentKey => segments[segmentKey]));
		toRemove.clear();
		removedKeys.forEach(segmentKey => segments[segmentKey] = undefined);

		//check if the segments end on an existing segment (i.e. does the event vertex lie on an existing segment) (intersection type = VERTEX)
		//if it does, cut that segment at the event vertex
		//repeat until no lines can be found
		let emitCount = 0;
		do {
			emitCount = emittedSegments.length;
			emittedSegments.splice(emittedSegments.length, 0, ...processEventVertex(first, sweepLine, eventQueue, vertices, segments, emitCount));
		} while (emitCount < emittedSegments.length);

		emittedSegments.splice(0, 0, segments[sweepLine.delete(first)]);
		segments[first] = undefined;

		//check for segment(s)  that ran through the event vertex and lie between the 
		//removed segments (it could be only one segment at most because if it were 
		//more segments they would intersect and be handle by the intersection handler)
		let newNext = prev != undefined ? sweepLine.next(prev) : undefined;
		let newPrev = next != undefined ? sweepLine.prev(next) : undefined;

		if (newNext != undefined && next != undefined && segments[newNext].sourceId === segments[next].sourceId) {
			newNext = undefined;
		}
		if (newPrev != undefined && prev != undefined && segments[newPrev].sourceId === segments[prev].sourceId) {
			newPrev = undefined;
		}

		if (newNext != undefined) {
			doIntersectionTest(prev, newNext, eventQueue, vertices, segments, intersectionTester, intersectionDrawer);
		}
		if (newPrev != undefined) {
			doIntersectionTest(newPrev, next, eventQueue, vertices, segments, intersectionTester, intersectionDrawer);
		}
		if (newPrev === undefined && newNext === undefined) {
			//no segments emerge from the removed segments
			doIntersectionTest(prev, next, eventQueue, vertices, segments, intersectionTester, intersectionDrawer);
		}

	}
	return emittedSegments;
}

const cutSegment = (intersectedSegmentKey: number, vertices: Point2D[], segments: SegmentMap, clipVertexIndex: number, segmentIndex: number): { originalSegment: number, clippedSegment: Segment, remainingSegment: Segment } => {
	const intersectedSegment = segments[intersectedSegmentKey];
	if (!eqPoint(vertices[clipVertexIndex], vertices[intersectedSegment.iVertical0])) {
		const clippedSegment = mergeSegments({}, intersectedSegment);
		clippedSegment.iVertical1 = clipVertexIndex;

		if (!eqPoint(vertices[clipVertexIndex], vertices[intersectedSegment.iVertical1])) {
			//the remaining part has no zero length 
			const remainingSegment = mergeSegments({}, intersectedSegment);
			remainingSegment.iVertical0 = clipVertexIndex;
			remainingSegment.id = 'v' + clipVertexIndex + '_i' + segmentIndex;
			return { originalSegment: intersectedSegmentKey, clippedSegment: clippedSegment, remainingSegment: remainingSegment };
		} else {
			//clip vertex equals bottom vertex
			return { originalSegment: intersectedSegmentKey, clippedSegment: clippedSegment, remainingSegment: undefined };
		}
	} else {
		//clip vertex equals top vertex
		const remainingSegment = mergeSegments({}, intersectedSegment);
		return { originalSegment: intersectedSegmentKey, clippedSegment: undefined, remainingSegment: remainingSegment };
	}
}

const intersectSegments = (swEv: SweepLineEvent, sweepLine: BinarySearchTree<number>, eventQueue: BinarySearchTree<SweepLineEvent>, vertices: Point2D[], segments: SegmentMap, intersectionTester: (s0: number, s1: number) => IntersectionResult, performIntersectionTests: boolean, intersectionDrawer?: (vertices: Point2D[], s0: Segment, s1: Segment, i: Point2D) => void): Segment[] => {
	const emittedSegments: Segment[] = [];
	while (!swEv.intersect.isEmpty()) {

		let prev = sweepLine.prev(swEv.intersect.min());
		//make sure prev does not contain the event vertex (this can happens when it overlaps first)
		while (prev && pointOnSegment(vertices[segments[prev].iVertical0], vertices[segments[prev].iVertical1], swEv.vertex)) prev = sweepLine.prev(prev);
		let next = sweepLine.next(swEv.intersect.max());
		//make sure next does not contain the event vertex (this can happens when it overlaps last)
		while (next && pointOnSegment(vertices[segments[next].iVertical0], vertices[segments[next].iVertical1], swEv.vertex)) next = sweepLine.next(next);

		const intersectedSegments = Array.from(swEv.intersect.inOrder()).map(segment => sweepLine.getNode(segment).data);

		swEv.intersect.clear();

		//make the cuts
		const cutSegments = intersectedSegments.map((intersectedSegment, index) => cutSegment(intersectedSegment, vertices, segments, swEv.vertexIndex, index));

		//assemble the clipped segments
		emittedSegments.splice(0, 0, ...cutSegments.filter(cutSegment => cutSegment.clippedSegment).map(cutSegment => cutSegment.clippedSegment));

		//remove all intersecting segments
		cutSegments.forEach(cutSegment => sweepLine.delete(cutSegment.originalSegment));
		// add all remaining segments (i.e. all segments that have a none zero length after the point of intersection)
		const remainingSegments = cutSegments
			.filter(cutSegment => cutSegment.remainingSegment)
			.map(cutSegment => {
				//update the original segment to its shortened version'
				segments[cutSegment.originalSegment] = mergeSegments(segments[cutSegment.originalSegment], cutSegment.remainingSegment);
				//add the updated segment to the sweepline
				  const addedNode = sweepLine.add(cutSegment.originalSegment);
				  if(addedNode === undefined){
					console.log('TROUBLE %o', cutSegment);	
				 }
				 return addedNode.data;
//				return sweepLine.add(cutSegment.originalSegment).data;
			});

		if (performIntersectionTests) {
			//the last thing that needs to be done is intersection checking 
			if (remainingSegments.length > 0) {
				const last = remainingSegments[0];
				const first = remainingSegments[remainingSegments.length - 1];

				//check if the segments start on an existing segment (i.e. does the event vertex lie on an existing segment) (intersection type = VERTEX)
				//if it does, cut that segment at the event vertex
				//repeat until no lines can be found
				let emitCount = 0;
				do {
					emitCount = emittedSegments.length;
					emittedSegments.splice(emittedSegments.length, 0, ...processEventVertex(first, sweepLine, eventQueue, vertices, segments, emitCount));
				} while (emitCount < emittedSegments.length);

				let prev = sweepLine.prev(first);
				//make sure prev does not contain the event vertex (this can happens when it overlaps first)
				while (prev && pointOnSegment(vertices[segments[prev].iVertical0], vertices[segments[prev].iVertical1], swEv.vertex)) prev = sweepLine.prev(prev);
				let next = sweepLine.next(last);
				//make sure next does not contain the event vertex (this can happens when it overlaps last)
				while (next && pointOnSegment(vertices[segments[next].iVertical0], vertices[segments[next].iVertical1], swEv.vertex)) next = sweepLine.next(next);

				doIntersectionTest(prev, first, eventQueue, vertices, segments, intersectionTester, intersectionDrawer);
				doIntersectionTest(last, next, eventQueue, vertices, segments, intersectionTester, intersectionDrawer);
			} else {
				doIntersectionTest(prev, next, eventQueue, vertices, segments, intersectionTester, intersectionDrawer);
			}
		}
	}
	return emittedSegments;
}

const doIntersectionTest = (prevKey: number, nextKey: number, eventQueue: BinarySearchTree<SweepLineEvent>, vertices: Point2D[], segments: SegmentMap, intersectionTester: (s0: number, s1: number) => IntersectionResult, draw?: (vertices: Point2D[], s0: Segment, s1: Segment, i: Point2D) => void): SweepLineEvent => {
	const prev = prevKey !== undefined ? segments[prevKey] : undefined;
	const next = nextKey !== undefined ? segments[nextKey] : undefined;
	const intersection = prevKey && nextKey ? intersectionTester(prevKey, nextKey) : undefined;
	if (draw && prevKey && nextKey) {
		draw(vertices, prev, next, undefined);
	}
	//check if we got an intersection that is lower then the sweepline
	const currentEventVertex = eventQueue.min().vertex;
	if (intersection && intersection.type === IntersectionType.NORMAL && //existing normal intersection
		(intersection.i0[1] < currentEventVertex[1] || //that is lower then the sweep line
			(intersection.i0[1] == currentEventVertex[1] && intersection.i0[0] >= currentEventVertex[0]))) { //or on the sweepline but not left from the current event vertex
		const intersectedSegments: number[] = [];
		if (!eqPoint(intersection.i0, vertices[prev.iVertical0]) && !eqPoint(intersection.i0, vertices[prev.iVertical1])) {
			intersectedSegments.push(prevKey);
		}
		if (!eqPoint(intersection.i0, vertices[next.iVertical0]) && !eqPoint(intersection.i0, vertices[next.iVertical1])) {
			intersectedSegments.push(nextKey);
		}
		if (intersectedSegments.length > 0) {
			const intersectEvent: SweepLineEvent = {
				id: 'X_' + prev.sourceId + '::' + next.sourceId,
				vertex: intersection.i0,
				vertexIndex: vertices.length,
				vertexId: 'x' + prev.sourceId + '::' + next.sourceId,
				add: createSegmentLine(vertices, segments),
				remove: createSegmentLine(vertices, segments),
				intersect: createSegmentLine(vertices, segments, intersectedSegments)
			};

			let intersectEventNode = eventQueue.add(intersectEvent);
			if (intersectEventNode) {
				vertices.push(intersection.i0);
				//console.log('added new intersection event %o', intersectEventNode.data);
			} else {
				intersectEventNode = eventQueue.getNode(intersectEvent);
				//console.log('added intersection to existing event %o', intersectEventNode.data);
			}
			if (draw) draw(vertices, prev, next, intersection.i0);
			return intersectEventNode.data;
		}
	}
	return undefined;
}

const eventResolver = (nodeData: SweepLineEvent, newData: SweepLineEvent): void => {
	nodeData.add.addAll(newData.add);
	nodeData.remove.addAll(newData.remove);
	nodeData.intersect.addAll(newData.intersect);
}

const logEvents = (events: BinarySearchTree<SweepLineEvent>, lines: BinarySearchTree<number>, vertices: Point2D[], segments: SegmentMap): void => {
	console.log('Q: ' + events.toInOrderString(event => event.id));
	console.log('L: ' + lines.toInOrderString(line => line.toString()));

	console.log(sweepLineToDumpString(lines, segments));
	console.log(verticesToDumpString(vertices));
}

export class IntersectionSweeper {
	public vertices: Point2D[];
	public emittedSegments: Segment[];
	public events: BinarySearchTree<SweepLineEvent>;
	public sweepLine: BinarySearchTree<number>;
	public nextEvent: SweepLineEvent;
	public segments: SegmentMap;

	private intersectionTester: (s0: number, s1: number) => IntersectionResult;

	public constructor(vertices: Point2D[],
		segments: Segment[],
		private onEvent?: (message: SweepMessage) => void,
		private intersectionDrawer?: (vertices: Point2D[], s0: Segment, s1: Segment, i: Point2D) => void) {

//		this.vertices = JSON.parse(JSON.stringify(vertices))
//		this.segments = (<Segment[]>JSON.parse(JSON.stringify(segments))).reduce((sMap, segment) => {
		this.vertices = vertices;
		this.segments = segments.reduce((sMap, segment) => {
			sMap[segment.sourceId] = segment;
			return sMap;
		}, <SegmentMap>{});

		//setup initial event Queue
		//bind events factory to vertices
		let verts = this.vertices;
		const segmentMapper = segmentToSweepLineEvents.bind(this, verts, this.segments);
		this.events = new BinarySearchTree(sweepLineEventComparator, eventResolver);

		Object.keys(this.segments)
			.map(segment => segmentMapper(segment))
			.forEach(segmentEvents => {
				this.events.addAll(segmentEvents);
			});

		this.sweepLine = createSegmentLine(this.vertices, this.segments);

		//bind intersectionTester to vertices	
		this.intersectionTester = segmentIntersectionTester.bind(this, this.vertices, this.segments);

		this.emittedSegments = [];
		this.nextEvent = this.events.min();

	}
	public next(): boolean {
		if (this.nextEvent) {
			//			logEvents(this.events, this.sweepLine, this.vertices, this.segments);
			const result = handleEvent(this.nextEvent, this.sweepLine, this.events,  this.vertices, this.segments, this.intersectionTester, this.intersectionDrawer);
			this.emittedSegments.splice(this.emittedSegments.length, 0, ...result.segments);
			this.nextEvent = result.nextEvent;
			if (this.onEvent) {
				this.onEvent(result);
			}
			if (this.nextEvent == undefined) {
			}
		}
		return this.nextEvent != undefined;
	}

	public sweep(): void {
		while (this.next());
	}
}

export const getIntersectionSweeper = (
	vertices: Point2D[],
	segments: Segment[],
	onEvent?: (message: SweepMessage) => void,
	intersectionDrawer?: (vertices: Point2D[], s0: Segment, s1: Segment, i: Point2D) => void): IntersectionSweeper => {
	return new IntersectionSweeper(vertices, segments, onEvent, intersectionDrawer);
}
